import axios from "axios";
import { ClientStatus } from "../types/client";
import { Message } from "../types/chat";
import { SupportFlowDefinition, SupportFlowConfigResponse } from "../types/supportFlow";
import { chatHistoryService } from './chatHistoryService';
import { logout } from './authService';

// Dynamic API base URL determination
let API_BASE_URL = '';

// Check for environment variable first
if (process.env.REACT_APP_API_URL) {
    API_BASE_URL = process.env.REACT_APP_API_URL;
    console.log(`[API] Using REACT_APP_API_URL: ${API_BASE_URL}`);
} else if (typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    // For local development
    API_BASE_URL = 'http://localhost:8000';
    console.log(`[API] Running in local development mode with API_BASE_URL: ${API_BASE_URL}`);
} else {
    // For production/preview, use relative URLs with Vercel rewrites
    API_BASE_URL = '';
    console.log('[API] Using relative URLs (will be handled by Vercel rewrites)');
}

// Set up Axios interceptors for authentication
axios.interceptors.request.use(
    (config) => {
        // First check for impersonation token
        const impersonationActive = localStorage.getItem('impersonation_token') !== null;
        const token = localStorage.getItem('auth_token');
        
        console.log('[API] Request interceptor:', {
            url: config.url,
            hasToken: !!token,
            impersonationActive,
            token: token ? 'present' : 'missing'
        });
        
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            
            // If we're in impersonation mode, add a custom header
            if (impersonationActive) {
                config.headers['X-Impersonation-Active'] = 'true';
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add response interceptor to handle auth failures
axios.interceptors.response.use(
    (response) => response, // Return successful responses as-is
    (error) => {
        console.log('[API] Response interceptor error:', {
            status: error.response?.status,
            url: error.config?.url,
            hasToken: !!localStorage.getItem('auth_token')
        });
        
        // Check if the error is auth-related (401 Unauthorized or 403 Forbidden)
        const isAuthError = 
            error.response?.status === 401 || 
            error.response?.status === 403 ||
            error.response?.data?.detail === "Authorization header missing";
        
        if (isAuthError) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('user_role');
            console.log('[AUTH] Authentication failed, logging out user');
            
            // Use the centralized logout function
            logout('/login');
        }
        
        // Always reject the promise to propagate the error to the caller
        return Promise.reject(error);
    }
);

// Add TypeScript declaration for window globals used by widget
declare global {
    interface Window {
        WIDGET_API_URL?: string;
        IS_WIDGET_MODE?: boolean;
        SKIP_CITATION_API_CALLS?: boolean;
        SKIP_API_CALLS?: boolean;
        ALLOW_CALENDAR_API?: boolean;
        ALLOW_CHAT_API?: boolean;
    }
}

// For the widget, check if we're in widget mode and use the widget API URL
export const getApiBaseUrl = () => {
    if (window.WIDGET_API_URL) {
        // Check if this is for the chat stream endpoint and we're in production widget mode
        const isWidgetMode = window.IS_WIDGET_MODE === true;
        const isProd = typeof window !== 'undefined' && window.location.hostname !== 'localhost';
        
        // If we're in widget mode and in production, ensure we use the dedicated widget endpoint for chat-stream
        if (isWidgetMode && isProd) {
            console.log('[API] Using widget API URL:', window.WIDGET_API_URL);
            return window.WIDGET_API_URL;
        }
        
        return window.WIDGET_API_URL;
    }
    return `${API_BASE_URL}/api`; // Empty string for relative URL in main frontend
};

// Export a function to get the raw API base URL (without /api suffix)
export const getApiRootUrl = () => {
    return API_BASE_URL;
};

export const getDiscoveryDefaults = async () => {
    const response = await axios.get(`${getApiBaseUrl()}/crawl/discovery-defaults`);
    return response.data;
};

export const createClient = async (clientData: {
    name: string;
    prompt_display_name?: string;
    email: string;
    phone: string;
    website: string;
    password: string;
    bot_purpose?: string;
    bot_personality?: string;
    bot_type?: string;
    selected_pages?: string[];
}, onProgress?: (data: any) => void): Promise<any> => {
    try {
        console.log('Creating client with data:', clientData);
        // Extract consultant_id from JWT (authoritative source)
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Please login to create a client');
        }

        let consultantId: string | null = null;
        try {
            const payloadPart = token.split('.')[1];
            const decoded = JSON.parse(atob(payloadPart));
            consultantId = decoded?.consultant_id || null;
        } catch (e) {
            console.error('Failed to decode JWT for consultant_id', e);
        }

        if (!consultantId) {
            throw new Error('Consultant account not found on session. Please re-login.');
        }

        // Use demo endpoint for demo flows, consultant endpoint for real consultant flows
        const isDemoMode = !consultantId || window.location.hostname.includes('demo') || window.location.pathname.includes('/demo');
        const endpoint = isDemoMode
            ? `${API_BASE_URL}/api/demo-enhanced/create-client`  // Fixed: Added /api prefix
            : `${API_BASE_URL}/api/consultant/${consultantId}/clients`;

        const requestBody = isDemoMode
            ? {
                name: clientData.name,
                email: clientData.email,
                phone: clientData.phone,
                website: clientData.website,
                password: clientData.password,
                company_name: clientData.prompt_display_name || clientData.name,
                bot_purpose: clientData.bot_purpose || 'general',
                bot_personality: clientData.bot_personality || 'professional',
                bot_type: clientData.bot_type || null,
                selected_pages: clientData.selected_pages || [],
                session_id: localStorage.getItem('demo_session_id') || null
            }
            : {
                ...clientData,
                consultant_id: consultantId
            };

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        // Always add auth header - demo requires consultant authentication
        // token is already retrieved at line 131
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
        });

        const responseData = await response.json();
        console.log("Client creation response:", responseData);

        if (!response.ok) {
            // Handle specific error cases for better UX
            if (response.status === 409 || response.status === 400) {
                // Enhanced error response from backend
                if (typeof responseData.detail === 'object' && responseData.detail.message) {
                    const error: any = new Error(responseData.detail.message);
                    error.suggestion = responseData.detail.suggestion;
                    error.email = responseData.detail.email;
                    error.statusCode = response.status;
                    throw error;
                } else if (typeof responseData.detail === 'string') {
                    if (responseData.detail.toLowerCase().includes('email already') ||
                        responseData.detail.toLowerCase().includes('already registered')) {
                        const error: any = new Error('This email is already registered');
                        error.suggestion = 'Please use a different email address or login to your existing account';
                        error.statusCode = response.status;
                        throw error;
                    }
                }
            }
            throw new Error(responseData.detail || 'Failed to create client');
        }

        // If we have a client ID and progress callback, set up the stream
        if (responseData.client_id && onProgress) {
            const clientId = responseData.client_id;
            
            // Store the clientId in localStorage
            localStorage.setItem('client_id', clientId);
            
            // Create a stream connection
            const eventSource = createCrawlStatusStream(clientId, onProgress);
            
            // Attach the close method to the response so it can be closed later
            responseData.closeStream = () => eventSource.close();
        }

        return responseData;
    } catch (error: any) {
        console.error('Error creating client:', error);
        
        // Email already exists
        if (error.message.includes('Email already exists')) {
            throw new Error('This email address is already registered. Please use a different email address.');
        }
        
        // Session expired
        if (error.message.includes('Not authenticated') || error.message.includes('Session expired')) {
            throw new Error('Session expired. Please login again.');
        }
        
        // Fallback to the original error message
        throw error;
    }
};

export const configureDataSource = async (clientId: string, payload: any) => {
    const response = await axios.post(`${getApiBaseUrl()}/client/${clientId}/data-sources`, payload);
    return response.data;
};

export const fetchDataSourceStatus = async (clientId: string) => {
    const response = await axios.get(`${getApiBaseUrl()}/client/${clientId}/data-sources`);
    return response.data;
};

export const triggerDataSourceSync = async (clientId: string, sourceType: string) => {
    const response = await axios.post(`${getApiBaseUrl()}/client/${clientId}/data-sources/${sourceType}/sync`);
    return response.data;
};

export const submitSupportStep = async (payload: {
    client_id: string;
    session_id: string;
    step_id: string;
    answer: any;
    conversation_id?: string;
    guest_token?: string;
    user_id?: string;
}) => {
    const response = await axios.post(`${getApiBaseUrl()}/chat/support/step`, payload);
    return response.data;
};

export const getSupportFlowConfig = async (clientId: string): Promise<SupportFlowConfigResponse> => {
    const response = await axios.get(`${getApiBaseUrl()}/support/flows/${clientId}`);
    return response.data;
};

export const saveSupportFlowConfig = async (
    clientId: string,
    payload: {
        definition: SupportFlowDefinition;
        is_enabled: boolean;
        escalation_email?: string | null;
    }
) => {
    const response = await axios.post(`${getApiBaseUrl()}/support/flows/${clientId}`, payload);
    return response.data;
};

export const getClientStatus = async (clientId: string): Promise<ClientStatus> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/client/status/${clientId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const rescrapWebsite = async (
    clientId: string,
    url: string,
    onProgress?: (data: any) => void,
    skipDeletionAndReuse: boolean = false,
    scrapingMethod: 'internal' | 'firecrawl' = 'internal',
    enablePreview: boolean = false,
    botPurpose: string = 'general',
    customPrompt?: string,
    selectedPages?: string[],
    botType?: string
): Promise<any> => {
    if (!clientId) {
        throw new Error('Client ID is required');
    }

    try {
        const response = await axios.post(`${API_BASE_URL}/api/rescrap/${clientId}`, {
            client_id: clientId,
            url,
            skip_deletion_and_reuse: skipDeletionAndReuse,
            scraping_method: scrapingMethod,
            enable_preview: enablePreview,
            bot_purpose: botPurpose,
            bot_type: botType,
            custom_prompt: customPrompt,
            selected_pages: selectedPages
        });
        
        const responseData = response.data;

        // If progress tracking is requested, setup a stream
        if (onProgress) {
            // Create stream connection to track crawl progress
            const eventSource = createCrawlStatusStream(clientId, onProgress);
            
            // Attach the close method to the response
            responseData.closeStream = () => eventSource.close();
        }

        return responseData;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.message || 'Failed to start website crawl');
        }
        throw error;
    }
};

/**
 * Get current crawl status for a client
 * @param clientId The client ID
 * @returns The current crawl status
 */
// Keep track of pending requests to prevent duplicates
const pendingCrawlStatusRequests = new Map<string, Promise<any>>();
const throttleTimers = new Map<string, number>();

export const getCrawlStatus = async (clientId: string): Promise<any> => {
    // Return the existing promise if there's already a request in progress
    if (pendingCrawlStatusRequests.has(clientId)) {
        console.log(`[CRAWL-API] Reusing existing request for client ${clientId}`);
        return pendingCrawlStatusRequests.get(clientId);
    }
    
    // Check if we should throttle (within 2 seconds of last request)
    const lastRequestTime = throttleTimers.get(clientId) || 0;
    const now = Date.now();
    if (now - lastRequestTime < 2000) {
        console.log(`[CRAWL-API] Throttling getCrawlStatus for client ${clientId}`);
        // Return last promise if available, otherwise continue with new request
        if (pendingCrawlStatusRequests.has(clientId)) {
            return pendingCrawlStatusRequests.get(clientId);
        }
    }
    
    // Record the time of this request
    throttleTimers.set(clientId, now);
    
    try {
        // Create a new request promise
        const requestPromise = axios.get(`${API_BASE_URL}/api/crawl-status/${clientId}`)
            .then(response => {
                // On success, clear from pending map after a small delay
                setTimeout(() => {
                    pendingCrawlStatusRequests.delete(clientId);
                }, 100);
                return response.data;
            })
            .catch(error => {
                // On error, clear from pending map immediately
                pendingCrawlStatusRequests.delete(clientId);
                if (axios.isAxiosError(error)) {
                    throw new Error(error.response?.data?.detail || 'Failed to get crawl status');
                }
                throw error;
            });
        
        // Store the promise
        pendingCrawlStatusRequests.set(clientId, requestPromise);
        
        return requestPromise;
    } catch (error) {
        // This should never happen as errors are caught in the promise chain
        pendingCrawlStatusRequests.delete(clientId);
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to get crawl status');
        }
        throw error;
    }
};

/**
 * Get all active crawl jobs
 * @returns List of active crawl jobs
 */
export const getActiveCrawls = async (): Promise<any> => {
    return [];
    // Future implementation:
    // try {
    //     const response = await axios.get(`${API_BASE_URL}/api/crawl-status`);
    //     return response.data;
    // } catch (error: any) {
    //     if (axios.isAxiosError(error)) {
    //         throw new Error(error.response?.data?.detail || 'Failed to get active crawls');
    //     }
    //     throw error;
    // }
};

/**
 * Creates a server-sent event connection to monitor crawl status.
 * 
 * @param clientId Client ID to monitor
 * @param onMessage Optional callback for handling messages
 * @param onError Optional callback for errors
 * @param onComplete Optional callback when streaming completes
 * @returns EventSource object
 */
export const createCrawlStatusStream = (
    clientId: string,
    onMessage?: (data: any) => void,
    onError?: (error: any) => void,
    onComplete?: () => void
): EventSource => {
    // On Vercel, use the full URL to bypass buffering issues
    const streamUrl = `${getApiBaseUrl()}/crawl-status/${clientId}/stream`;
    
    console.log(`[CRAWL-API] Creating stream to: ${streamUrl}`);
    
    // Create and return EventSource directly to maintain backward compatibility
    const eventSource = new EventSource(streamUrl);
    
    // Flag to track if we've detected "no active crawl" message
    let noActiveCrawlDetected = false;
    
    // If callbacks are provided, connect them
    if (onMessage) {
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                // Check for "No active crawl" error message
                if (data.error && data.error.includes("No active crawl for this client")) {
                    console.log(`[CRAWL-API] No active crawl detected for client ${clientId}, closing stream`);
                    noActiveCrawlDetected = true;
                    eventSource.close(); // Close the connection to prevent automatic reconnection
                    
                    // Still call onMessage so client can handle this status
                    onMessage(data);
                    return;
                }
                
                // Check if the crawl was stopped or aborted
                if (data.status === 'stopped' || data.status === 'aborted') {
                    console.log(`[CRAWL-API] Crawl ${data.status} for client ${clientId}`);
                    // Don't close immediately - let the server send the close event
                }
                
                // Process normal messages
                onMessage(data);
            } catch (e) {
                console.error(`[CRAWL-API] Error parsing event data:`, e);
            }
        };
        
        // Listen for "status" events (backend sends custom event type)
        eventSource.addEventListener('status', (event) => {
            try {
                const data = JSON.parse(event.data);
                
                // Same error handling as onmessage
                if (data.error && data.error.includes("No active crawl for this client")) {
                    console.log(`[CRAWL-API] No active crawl detected for client ${clientId}, closing stream`);
                    noActiveCrawlDetected = true;
                    eventSource.close();
                    onMessage(data);
                    return;
                }
                
                if (data.status === 'stopped' || data.status === 'aborted') {
                    console.log(`[CRAWL-API] Crawl ${data.status} for client ${clientId}`);
                }
                
                // Process status updates
                onMessage(data);
            } catch (e) {
                console.error(`[CRAWL-API] Error parsing status event data:`, e);
            }
        });
    }
    
    // Listen for the close event sent by the server
    eventSource.addEventListener('close', (event) => {
        console.log(`[CRAWL-API] Received close event for client ${clientId}`);
        eventSource.close();
        if (onComplete) {
            onComplete();
        }
    });
    
    if (onComplete) {
        eventSource.addEventListener('complete', () => {
            console.log(`[CRAWL-API] Stream complete for client ${clientId}`);
            onComplete();
        });
    }
    
    // Custom error handler to prevent automatic reconnection for "No active crawl"
    const originalOnerror = eventSource.onerror;
    eventSource.onerror = (error) => {
        // If we've already detected "no active crawl", don't try to reconnect
        if (noActiveCrawlDetected) {
            console.log(`[CRAWL-API] Preventing reconnection after "No active crawl" message`);
            
            // Call close again just to be sure
            eventSource.close();
            
            // Call error callback if provided
            if (onError) onError(error);
            return;
        }
        
        console.error(`[CRAWL-API] Stream error:`, error);
        
        // Call the provided error callback
        if (onError) onError(error);
        
        // Call the original error handler if it exists (keeps compatibility with original behavior)
        if (originalOnerror && !noActiveCrawlDetected) originalOnerror.call(eventSource, error);
    };
    
    return eventSource;
};

export const stopCrawl = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/crawl-status/${clientId}/stop`);
        return response.data;
    } catch (error) {
        console.error('Error stopping crawl:', error);
        throw error;
    }
};

export const abortCrawl = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/crawl-status/${clientId}/abort`);
        return response.data;
    } catch (error) {
        console.error('Error aborting crawl:', error);
        throw error;
    }
};

export const sendChatMessage = async (clientId: string, message: string, contextData = {}) => {
    try {
        // Retrieve chat history for this client
        const chatHistory = chatHistoryService.getHistory(clientId);
        
        // Only include a reasonable number of messages for context
        const recentHistory = chatHistory.slice(-10);
        
        // Format history for API request
        const formattedHistory = recentHistory.map((msg: Message) => ({
            role: msg.role,
            content: msg.content
        }));
        
        const response = await axios.post(`${getApiBaseUrl()}/chat`, {
            client_id: clientId,
            message,
            history: formattedHistory,
            context: {
                ...contextData,
                hasHistory: formattedHistory.length > 0
            }
        });

        return response.data;
    } catch (error) {
        console.error('Error calling chat API:', error);
        throw error;
    }
};

/**
 * Create a streaming chat connection using Server-Sent Events
 * @param clientId The client ID
 * @param message The message to send
 * @param onChunk Callback for each chunk received
 * @param onComplete Callback when the stream is complete
 * @param onError Callback for errors
 * @returns A function to close the stream
 */
export const createChatStream = (
    clientId: string, 
    message: string, 
    onChunk: (chunk: any) => void,
    onComplete?: (fullResponse: any) => void,
    onError?: (error: any) => void,
    extraMetadata?: Record<string, any>,
    overrideUrl?: string
): () => void => {
    // Store full response as we receive chunks
    let fullResponse = {
        content: '',
        type: 'text',
        isFormatted: false,
        metadata: {},
        citation_sources: [],
        token_data: null
    };
    
    // Flag to track whether we detected a clear history request
    let foundClearHistoryRequest = false;

    // Flag to track whether we already received a complete event
    let receivedCompleteEvent = false;

    // Get the conversation ID if stored in localStorage
    const conversationId = localStorage.getItem('conversation_id');
    
    // Create a POST request to the chat stream endpoint
    const postData: Record<string, any> = {
        client_id: clientId,
        message,
        // Include conversation_id in the message body for the updated ChatMessage model
        conversation_id: conversationId || undefined,
        // Include metadata for additional identification
        metadata: {}
    };
    
    // Merge any extra metadata (e.g., qa_run flags, image uploads)
    if (extraMetadata && typeof extraMetadata === 'object') {
        console.log('[CHAT-API] Merging extraMetadata into postData:', extraMetadata);
        postData.metadata = { ...postData.metadata, ...extraMetadata };
    }
    
    // Generate headers including guest identification
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
    };
    
    // Check if user is authenticated
    const authToken = localStorage.getItem('auth_token');
    
    if (authToken) {
        // For authenticated users, add auth header and don't send guest token
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('[CHAT-API] Authenticated user detected, using auth token');
    } else {
        // Only add guest token for non-authenticated users
        const guestToken = localStorage.getItem('guest_token');
        if (guestToken) {
            headers['X-Guest-Token'] = guestToken;
            // Also add to metadata
            postData.metadata.guest_token = guestToken;
        } else {
            // Generate a new guest token if none exists
            const newGuestToken = 'guest_' + Math.random().toString(36).substring(2, 15);
            localStorage.setItem('guest_token', newGuestToken);
            headers['X-Guest-Token'] = newGuestToken;
            // Also add to metadata
            postData.metadata.guest_token = newGuestToken;
        }
    }
    
    // Add widget mode header if in widget mode
    if (window.IS_WIDGET_MODE) {
        headers['X-Widget-Mode'] = 'true';
        console.log('[CHAT-API] Adding widget mode header');
    }
    
    // Add browser fingerprint for more stable guest identification
    const fingerprint = generateFingerprint();
    headers['X-Fingerprint'] = fingerprint;
    
    // Use different URLs based on environment; allow override
    const apiUrl = overrideUrl || `${getApiBaseUrl()}/chat/stream`;
    
    // Create controller for aborting the fetch if needed
    const controller = new AbortController();
    const { signal } = controller;
    
    // Buffer used to hold any partial SSE frame data that may be split across
    // network packets. This prevents JSON.parse errors caused by truncated
    // frames when the server / proxy splits a frame in the middle of its
    // payload (common in production behind Nginx / ELB).
    let eventBuffer = '';

    console.log(`[CHAT-API] Starting streaming chat request to: ${apiUrl}`);
    console.log(`[CHAT-API] Using conversation_id: ${conversationId || 'none (new conversation)'}`);
    console.log(`[CHAT-API] POST data being sent:`, postData);

    fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(postData),
        signal,
        credentials: 'omit',  // Change to 'omit' for cross-origin requests without credentials
        mode: 'cors'  // Explicitly set CORS mode
    }).then(response => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Create a reader for the response body stream
        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Could not get response reader');
        }
        
        // Function to read the next chunk
        const readChunk = async () => {
            try {
                const { done, value } = await reader.read();
                
                if (done) {
                    console.log(`[CHAT-API] Stream complete for client ${clientId}`);

                    // Only call onComplete if we didn't already receive a complete event
                    // (Support flows and other responses send a complete event, so we shouldn't call it twice)
                    if (!receivedCompleteEvent) {
                        console.log('[CHAT-API] No complete event received, calling onComplete with accumulated response');
                        onComplete?.(fullResponse);
                    } else {
                        console.log('[CHAT-API] Already received complete event, skipping onComplete call');
                    }
                    return;
                }
                
                // Convert the chunk to text
                const text = new TextDecoder().decode(value);

                // Append to buffer and split only on full frame delimiter
                eventBuffer += text;
                const frames = eventBuffer.split('\n\n');
                // The last element may be an incomplete frame – keep it in buffer
                eventBuffer = frames.pop() || '';

                // Process all complete frames
                for (const event of frames) {
                    // Parse the event
                    const eventLines = event.split('\n');
                    let eventName = 'message';
                    let eventData = '';
                    
                    for (const line of eventLines) {
                        if (line.startsWith('event:')) {
                            eventName = line.slice(6).trim();
                        } else if (line.startsWith('data:')) {
                            eventData = line.slice(5).trim();
                        }
                    }
                    
                    // Parse the data
                    if (eventData) {
                        try {
                            const data = JSON.parse(eventData);
                            
                            // Check for clear history flag before normal processing
                            const isClearHistoryRequest = 
                                data.metadata?.clear_local_storage === true || 
                                (data.metadata?.intent === 'system' && 
                                 data.content?.toLowerCase().includes('cleared your chat history'));
                            
                            if (isClearHistoryRequest) {
                                console.log('[CHAT-API] Detected clear history response:', data);
                                foundClearHistoryRequest = true;
                                
                                // Special handling for clear history requests
                                try {
                                    // Process this chunk first (but don't continue processing stream)
                                    onChunk(data);
                                    
                                    // Clear localStorage history
                                    chatHistoryService.clearHistory(clientId);
                                    console.log('[CHAT-API] Cleared chat history from localStorage');
                                    
                                    // Create a final response with clear metadata
                                    const clearResponse = {
                                        content: data.content,
                                        type: 'text',
                                        isFormatted: false,
                                        metadata: {
                                            ...data.metadata,
                                            clear_local_storage: true,
                                            intent: 'system'
                                        }
                                    };
                                    
                                    // Trigger complete callback immediately
                                    if (onComplete) {
                                        onComplete(clearResponse);
                                    }
                                    
                                    // Notify about localStorage change
                                    window.dispatchEvent(new Event('storage'));
                                    console.log('[CHAT-API] Dispatched storage event');
                                    
                                    // Stop reading from the stream - we've handled the clear request
                                    controller.abort();
                                    return;
                                } catch (clearError) {
                                    console.error('[CHAT-API] Error handling clear history:', clearError);
                                }
                            }
                            
                            // Handle metadata and conversation_id
                            if (data.metadata) {
                                // Check if we need to clear localStorage history
                                if (data.metadata.clear_local_storage) {
                                    console.log('[CHAT-API] Received clear_local_storage flag!', data.metadata);
                                    console.log('[CHAT-API] Clearing chat history from localStorage');
                                    
                                    // Don't continue processing this chunk - it will be handled above
                                    if (foundClearHistoryRequest) {
                                        return;
                                    }
                                    
                                    try {
                                        // Clear the history
                                        chatHistoryService.clearHistory(clientId);
                                        
                                        // Store new conversation id if provided
                                        if (data.metadata.conversation_id) {
                                            localStorage.setItem('conversation_id', data.metadata.conversation_id);
                                        }
                                        
                                        // Force refresh of local storage on the window object
                                        window.dispatchEvent(new Event('storage'));
                                        
                                        // Additional notification for debugging
                                        console.log(`[CHAT-API] Dispatched storage event to notify components of localStorage change`);
                                    } catch (error) {
                                        console.error('[CHAT-API] Error clearing localStorage:', error);
                                        
                                        // Try fallback direct removal as last resort
                                        try {
                                            const fallbackKey = 'chat_history';
                                            console.log('[CHAT-API] Attempting fallback direct localStorage removal');
                                            const rawData = localStorage.getItem(fallbackKey);
                                            
                                            if (rawData) {
                                                const data = JSON.parse(rawData);
                                                const filtered = data.filter((item: any) => item.clientId !== clientId);
                                                localStorage.setItem(fallbackKey, JSON.stringify(filtered));
                                                console.log('[CHAT-API] Fallback direct removal complete');
                                                
                                                // Notify components
                                                window.dispatchEvent(new Event('storage'));
                                            }
                                        } catch (fallbackError) {
                                            console.error('[CHAT-API] Fallback clearing failed:', fallbackError);
                                        }
                                    }
                                }
                                
                                // Always store conversation_id if present
                                if (data.metadata.conversation_id && !foundClearHistoryRequest) {
                                    localStorage.setItem('conversation_id', data.metadata.conversation_id);
                                }
                            }
                            
                            // If this is a clear history request that we've already detected,
                            // stop processing further chunks to avoid React DOM conflicts
                            if (foundClearHistoryRequest) {
                                console.log('[CHAT-API] Skipping further processing after clear history');
                                return;
                            }

                            // Handle different event types
                            if (eventName === 'complete') {
                                console.log(`[CHAT-API] Received complete event`, {
                                    intent: data.metadata?.intent,
                                    image_upload_needed: data.metadata?.image_upload_needed,
                                    image_step: data.metadata?.image_step,
                                    fullData: data
                                });

                                // Mark that we received a complete event
                                receivedCompleteEvent = true;

                                // If this is a clear history response, make sure we indicate that
                                if (data.metadata?.clear_local_storage ||
                                    (data.metadata?.intent === 'system' &&
                                     data.content?.toLowerCase().includes('cleared your chat history'))) {
                                    // Add clear metadata if not already present
                                    if (!data.metadata?.clear_local_storage) {
                                        data.metadata = data.metadata || {};
                                        data.metadata.clear_local_storage = true;
                                    }

                                    foundClearHistoryRequest = true;
                                }

                                // Merge complete event data with accumulated fullResponse
                                const completeResponse = {
                                    ...fullResponse,
                                    // Override with final data from complete event
                                    content: data.content || fullResponse.content,
                                    type: data.type || fullResponse.type,
                                    isFormatted: data.is_formatted !== undefined ? data.is_formatted : fullResponse.isFormatted,
                                    metadata: {
                                        ...fullResponse.metadata,
                                        ...data.metadata
                                    },
                                    // Use citation_sources from complete event (filtered and renumbered)
                                    citation_sources: data.metadata?.citation_sources || data.citation_sources,
                                    // Include token usage data if available
                                    token_data: data.metadata?.token_data || data.token_data || fullResponse.token_data
                                };

                                console.log(`[CHAT-API] Complete response citation sources:`, completeResponse.citation_sources?.length || 0);
                                console.log(`[CHAT-API] Complete response content length:`, completeResponse.content?.length || 0);
                                console.log(`[CHAT-API] About to call onChunk and onComplete callbacks`, {
                                    foundClearHistoryRequest,
                                    hasOnComplete: !!onComplete
                                });

                                // Log token usage if available
                                if (completeResponse.token_data) {
                                    console.log(`[CHAT-API] Token usage:`, completeResponse.token_data);
                                }

                                // Call the chunk callback first (for support flow image upload detection, etc.)
                                if (!foundClearHistoryRequest) {
                                    console.log('[CHAT-API] Calling onChunk with complete data');
                                    onChunk(data);
                                }

                                // Call the complete callback with merged data
                                if (onComplete && !foundClearHistoryRequest) {
                                    console.log('[CHAT-API] Calling onComplete callback');
                                    onComplete(completeResponse);
                                } else {
                                    console.log('[CHAT-API] NOT calling onComplete:', { hasOnComplete: !!onComplete, foundClearHistoryRequest });
                                }
                            } else if (eventName === 'error') {
                                console.error(`[CHAT-API] Error event:`, data);
                                onError?.(data.error || 'Unknown error');
                            } else {
                                // Regular message - only process if not a clear history request
                                if (!foundClearHistoryRequest) {
                                    console.log(`[CHAT-API] Received chunk:`, data.type);
                                    
                                    // Process chunk normally
                                    if (data.type === 'text_chunk') {
                                        // Update the full response content
                                        fullResponse.content += data.content;
                                        
                                        // First chunk might have metadata
                                        if (data.metadata) {
                                            fullResponse.metadata = { ...fullResponse.metadata, ...data.metadata };
                                            
                                            // Check if this is a formatted response
                                            if (data.metadata.use_formatted_response) {
                                                fullResponse.isFormatted = true;
                                            }
                                            
                                            // Skip citation sources from streaming chunks - use only from complete event
                                            
                                            // Capture token usage data from chunks
                                            if (data.metadata.token_data) {
                                                fullResponse.token_data = data.metadata.token_data;
                                                console.log(`[CHAT-API] Received token usage data in text_chunk:`, data.metadata.token_data);
                                            }
                                        }
                                    } else if (data.type === 'text') {
                                        // Final content possibly with formatting
                                        fullResponse.content = data.content;
                                        fullResponse.isFormatted = data.is_formatted;
                                        
                                        // Store metadata from complete event if present
                                        if (data.metadata) {
                                            fullResponse.metadata = { ...fullResponse.metadata, ...data.metadata };
                                            
                                            // Include citation sources from final text event
                                            if (data.metadata.citation_sources && Array.isArray(data.metadata.citation_sources)) {
                                                fullResponse.citation_sources = data.metadata.citation_sources;
                                            }
                                        }
                                        
                                        // Also check for citation_sources at top level
                                        if (data.citation_sources && Array.isArray(data.citation_sources)) {
                                            fullResponse.citation_sources = data.citation_sources;
                                        }
                                        
                                        // Also check for token_data at top level
                                        if (data.token_data) {
                                            fullResponse.token_data = data.token_data;
                                            console.log(`[CHAT-API] Received token usage data in text event:`, data.token_data);
                                        }
                                    } else if (data.type === 'booking') {
                                        // Calendar booking response
                                        fullResponse = {
                                            ...data.content,
                                            type: 'booking'
                                        };
                                    } else if (data.type === 'error') {
                                        fullResponse.type = 'error';
                                        fullResponse.content = data.content;
                                        onError?.(data.error || 'Unknown error');
                                    } else if (data.type === 'clear_history') {
                                        // Direct clear history response
                                        foundClearHistoryRequest = true;
                                        data.metadata = data.metadata || {};
                                        data.metadata.clear_local_storage = true;
                                        
                                        // Process this specifically
                                        onChunk(data);
                                        
                                        // And trigger completion
                                        if (onComplete) {
                                            onComplete({
                                                content: data.message || 'Chat history cleared',
                                                type: 'text',
                                                isFormatted: false,
                                                metadata: {
                                                    clear_local_storage: true,
                                                    intent: 'system'
                                                }
                                            });
                                        }
                                        
                                        // Don't process more chunks
                                        return;
                                    }
                                    
                                    // Call the chunk callback unless it's a clear history request we've already processed
                                    onChunk(data);
                                }
                            }
                        } catch (e) {
                            console.error(`[CHAT-API] Error parsing event data:`, e, eventData);
                        }
                    }
                }
                
                // If this was a clear history request, don't continue reading chunks
                if (foundClearHistoryRequest) {
                    console.log('[CHAT-API] Stopping stream after processing clear history');
                    return;
                }
                
                // Continue reading chunks if not a clear history request
                readChunk();
            } catch (error: any) {
                if (error.name === 'AbortError') {
                    console.log(`[CHAT-API] Stream aborted for client ${clientId}`);
                } else {
                    console.error(`[CHAT-API] Error reading stream:`, error);
                    // Ensure error is in proper format before passing to onError
                    const errorMessage = error?.message || 'Unknown stream error';
                    if (onError) {
                        try {
                            onError(errorMessage);
                        } catch (callbackError) {
                            console.error('[CHAT-API] Error in onError callback:', callbackError);
                        }
                    }
                }
            }
        };
        
        // Start reading chunks
        readChunk();
    }).catch(error => {
        console.error(`[CHAT-API] Fetch error:`, error);
        // Ensure error is in proper format before passing to onError
        const errorMessage = error?.message || 'Unknown fetch error';
        if (onError) {
            try {
                onError(errorMessage);
            } catch (callbackError) {
                console.error('[CHAT-API] Error in onError callback:', callbackError);
            }
        }
    });
    
    // Return a function to abort the stream
    return () => {
        console.log(`[CHAT-API] Aborting stream for client ${clientId}`);
        controller.abort();
    };
};

// Helper function to generate a simple browser fingerprint
function generateFingerprint(): string {
    const components = [
        navigator.userAgent,
        navigator.language,
        window.screen.height,
        window.screen.width,
        window.screen.colorDepth,
        new Date().getTimezoneOffset()
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < components.length; i++) {
        const char = components.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    
    return hash.toString();
}

export const login = async (email: string, password: string) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
            email,
            password
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error('Login error response:', error.response?.data);
            console.error('Login error status:', error.response?.status);
        }
        throw error;
    }
};

export const logoutUser = (redirectPath = '/login') => {
    try {
        // Use the already imported logout function
        logout(redirectPath);
    } catch (error) {
        console.error('Logout error:', error);

        // Fallback logout mechanism if the import fails - clear ALL localStorage
        localStorage.clear();

        if (typeof window !== 'undefined') {
            window.location.href = redirectPath;
        }
    }
};

export const getEmbeddingStats = async (clientId: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/embeddings/stats/${clientId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch embedding stats');
    }
};

export const getDashboardStats = async (clientId: string | null) => {
    try {
        const url = clientId 
            ? `${API_BASE_URL}/api/dashboard/stats/${clientId}`
            : `${API_BASE_URL}/api/dashboard/stats`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch dashboard stats');
    }
};

export const getTokenUsage = async (clientId: string, days: number = 30) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/dashboard/token-usage/${clientId}?days=${days}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch token usage');
    }
};

export const getDailyTokenUsage = async (clientId: string, days: number = 7) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/dashboard/token-usage/${clientId}/daily?days=${days}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch daily token usage');
    }
};

export const getDocuments = async (clientId: string) => {
    try {
        // Verify we're using the correct client ID
        const storedClientId = localStorage.getItem('client_id');
        if (clientId !== storedClientId) {
            console.warn('Client ID mismatch', { provided: clientId, stored: storedClientId });
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/documents/client/${clientId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch documents');
    }
};

export const getKnowledgeBase = async (clientId: string) => {
    try {
        console.log(`Fetching knowledge base for client: ${clientId}`);
        const response = await axios.get(`${API_BASE_URL}/api/scraped-content/${clientId}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
            }
        });
        console.log('Raw API response:', response);
        console.log('Response data:', response.data);
        
        // Return the response data directly since it's already in the correct format
        return response.data;
    } catch (error: any) {
        console.error('Knowledge base API error:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
        }
        throw error;
    }
};

export const getKnowledgeBasePageDetail = async (
    clientId: string,
    url: string
) => {
    try {
        const params = new URLSearchParams({ url });
        const response = await axios.get(
            `${API_BASE_URL}/api/scraped-content/${clientId}/page?${params.toString()}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error: any) {
        console.error('Knowledge base page detail API error:', error);
        throw error;
    }
};

export const getKnowledgeBasePages = async (
    clientId: string, 
    page: number = 1, 
    limit: number = 50,
    search?: string
) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        
        if (search) {
            params.append('search', search);
        }
        
        const response = await axios.get(
            `${API_BASE_URL}/api/scraped-content/${clientId}/pages-metadata?${params}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        
        return response.data;
    } catch (error: any) {
        console.error('Knowledge base pages API error:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
        }
        throw error;
    }
};

export const resetData = async () => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/reset-data`);
        return response.data;
    } catch (error) {
        console.error('Error resetting data:', error);
        throw error;
    }
};

export const uploadDocuments = async (formData: FormData): Promise<void> => {
    try {
        await axios.post(`${API_BASE_URL}/api/documents/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to upload documents');
        }
        throw error;
    }
};

export const getClientProfile = async (clientId: string) => {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get(`${baseUrl}/client/${clientId}/profile`);
    return response.data;
};

export const updateClientProfile = async (clientId: string, profileData: any) => {
    const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/profile`, profileData);
    return response.data;
};

export const uploadProfileImage = async (clientId: string, formData: FormData) => {
    const response = await axios.post(`${API_BASE_URL}/api/client/${clientId}/profile-image`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};

export const getClientPrompts = async (clientId: string) => {
    const response = await axios.get(`${API_BASE_URL}/api/client/${clientId}/prompts`);
    return response.data;
};

export const updateClientPrompts = async (clientId: string, prompts: any[]) => {
    const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/prompts`, prompts);
    return response.data;
};

export const resetClientPrompts = async (clientId: string) => {
    try {
        // First get the client profile to get the client name
        const clientProfile = await getClientProfile(clientId);
        const clientName = clientProfile.name || '';
        
        // Call the reset endpoint with client name
        const response = await axios.post(`${API_BASE_URL}/api/client/${clientId}/prompts/reset`, {
            client_name: clientName
        });
        
        return response.data;
    } catch (error) {
        console.error('Error resetting prompts:', error);
        throw error;
    }
};

export const updateClientPersonality = async (clientId: string, personality: string) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/personality`, {
            personality: personality
        });
        return response.data;
    } catch (error) {
        console.error('Error updating personality:', error);
        throw error;
    }
};

export const getClientSettings = async (clientId: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/client/${clientId}/settings`);
        return response.data;
    } catch (error) {
        console.error('Error getting client settings:', error);
        throw error;
    }
};

export const updateClientSettings = async (clientId: string, settings: any) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/settings`, settings);
        return response.data;
    } catch (error) {
        console.error('Error updating client settings:', error);
        throw error;
    }
};

export const requestPasswordReset = async (email: string) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/forgot-password`, { email });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const resetPassword = async (token: string, new_password: string) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/reset-password`, {
            token,
            new_password
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateClientPassword = async (clientId: string, passwordData: any) => {
    const response = await axios.put(`${API_BASE_URL}/api/${clientId}/password`, passwordData);
    return response.data;
};

export const getEmbeddings = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/embeddings`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
    });
    return response.data;
};

// Add these calendar-related API functions
export const connectCalendar = async (data: { username: string; apiKey: string; clientId: string }) => {
    try {
        console.log(`Connecting calendar for client ${data.clientId} with username ${data.username}`);
        const response = await axios.post(`${API_BASE_URL}/api/calendar/connect`, {
            username: data.username,
            api_key: data.apiKey,
            client_id: data.clientId
        });
        console.log('Calendar connection response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Calendar connection error:', error);
        if (axios.isAxiosError(error)) {
            // Handle both 400 and 500 errors
            const errorMessage = error.response?.data?.detail || 'Failed to connect calendar';
            throw new Error(errorMessage);
        }
        throw error;
    }
};

export const getCalendarStatus = async (clientId: string) => {
    try {
        console.log(`Getting calendar status for client ${clientId}`);
        const response = await axios.get(`${API_BASE_URL}/api/calendar/status?client_id=${clientId}`);
        
        // Enhanced logging for debugging
        if (response.data?.event_types?.length > 0) {
            console.log(`Retrieved ${response.data.event_types.length} event types`);
            console.log('First event type:', response.data.event_types[0]);
            
            // Log schedule information for debugging
            const firstEvent = response.data.event_types[0];
            if (firstEvent?.schedule?.days?.length > 0) {
                console.log('First day in schedule:', firstEvent.schedule.days[0]);
                
                // Log available time slots 
                if (firstEvent.schedule.days[0].available_hours) {
                    console.log('Available hours:', firstEvent.schedule.days[0].available_hours);
                }
                
                if (firstEvent.schedule.days[0].duration_slots) {
                    console.log('Duration-specific slots:', firstEvent.schedule.days[0].duration_slots);
                }
            }
        } else {
            console.warn('No event types found in calendar status response');
        }
        
        // Ensure the response has the expected structure
        const calendarStatus = {
            ...response.data,
            connected: response.data.connected === true,
            event_types: response.data.event_types || [],
            timezone: response.data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        
        // Process event types to ensure they have the correct structure for CalendarView
        if (calendarStatus.event_types && Array.isArray(calendarStatus.event_types)) {
            calendarStatus.event_types = calendarStatus.event_types.map((event: any) => {
                // Ensure each event has the required fields
                const processedEvent = {
                    ...event,
                    id: event.id || event.schedule_id || `event-${Math.random().toString(36).substring(2, 9)}`,
                    title: event.title || event.name || 'Appointment',
                    // Make sure schedule is properly structured
                    schedule: event.schedule || {
                        days: event.days || []
                    }
                };
                
                // Ensure schedule.days has the correct structure
                if (processedEvent.schedule && processedEvent.schedule.days) {
                    processedEvent.schedule.days = processedEvent.schedule.days.map((day: any) => {
                        // Don't modify the original time slots data
                        const processedDay = { ...day };
                        
                        // If we have no time slots but have duration-specific slots,
                        // extract available hours from them
                        if ((!processedDay.available_hours || processedDay.available_hours.length === 0) && 
                            processedDay.duration_slots && Object.keys(processedDay.duration_slots).length > 0) {
                            
                            // Get the first available duration slots
                            const firstDuration = Object.keys(processedDay.duration_slots)[0];
                            
                            // IMPORTANT FIX: Don't modify the original slots - use them exactly as provided
                            // This ensures we don't show slots beyond what Cal.com API provides
                            processedDay.available_hours = [...processedDay.duration_slots[firstDuration]];
                            
                            console.log(`[API DEBUG] Generated available_hours from duration_slots for day ${processedDay.date || processedDay.day}:`, 
                                        processedDay.available_hours);
                            console.log(`[API DEBUG] Original duration_slots keys: ${Object.keys(processedDay.duration_slots).join(', ')}`);
                            console.log(`[API DEBUG] Duration slots data: `, processedDay.duration_slots);
                            
                            // Ensure the original data remains available
                            processedDay._original_duration_slots = {...processedDay.duration_slots};
                        }
                        
                        // Ensure available_hours_utc property is set
                        if (processedDay.available_hours_utc === undefined) {
                            // Check if this is a Cal.com response by looking for typical Cal.com properties
                            const isCalComFormat = processedDay.weekday !== undefined || 
                                                  processedDay.duration_slots !== undefined;
                            
                            // If it appears to be Cal.com data, default to true (Cal.com uses UTC)
                            processedDay.available_hours_utc = isCalComFormat ? true : false;
                            console.log(`[API DEBUG] Set available_hours_utc to ${processedDay.available_hours_utc} for day ${processedDay.date || processedDay.day}`);
                            
                            // Add timezone debugging info
                            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                            const userOffset = new Date().getTimezoneOffset();
                            console.log(`[API DEBUG] User timezone: ${userTimezone}, UTC offset: ${-userOffset/60} hours`);
                        }
                        
                        return processedDay;
                    });
                }
                
                return processedEvent;
            });
            
            console.log('Processed event types for CalendarView:', calendarStatus.event_types);
        }
        
        return calendarStatus;
    } catch (error) {
        console.error('Error getting calendar status:', error);
        // Don't swallow the error - log and return a default response
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            console.log('Calendar not found for client - returning disconnected state');
            return { connected: false, event_types: [], timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
        }
        
        // For other errors, still throw so the UI can handle them properly
        throw error;
    }
};

export const bookMeeting = async (bookingData: {
  event_type_id: string;
  user_name: string;
  user_email: string;
  start_time: string;
  duration: number;
  client_id: string;
  notes?: string;
  timezone?: string;
}) => {
  try {
    console.log('[API] ===== BOOKING MEETING WITH CAL.COM V1 API =====');
    console.log(`[API] Time to book: ${bookingData.start_time}`);
    console.log(`[API] Event Type ID: ${bookingData.event_type_id}`);
    console.log(`[API] Client timezone: ${bookingData.timezone || 'UTC'}`);
    
    // Get client ID
    const clientId = bookingData.client_id || localStorage.getItem('client_id') || '';
    console.log(`[API] Using client ID: ${clientId}`);
    
    // API URL
    const apiUrl = `${API_BASE_URL}/api/book_meeting`;
    console.log(`[API] Booking URL: ${apiUrl}`);
    
    // Calculate end time if we have a duration
    let endTime = null;
    if (bookingData.duration) {
      try {
        // Parse the start time
        const startDate = new Date(bookingData.start_time);
        // Add the duration in minutes
        const endDate = new Date(startDate.getTime() + bookingData.duration * 60000);
        // Format the end time in the same format as the start time
        // Use the manual formatting to ensure consistent format
        const year = endDate.getUTCFullYear();
        const month = String(endDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(endDate.getUTCDate()).padStart(2, '0');
        const hour = String(endDate.getUTCHours()).padStart(2, '0');
        const minute = String(endDate.getUTCMinutes()).padStart(2, '0');
        endTime = `${year}-${month}-${day}T${hour}:${minute}:00.000Z`;
        console.log(`[API] Calculated end time: ${endTime}`);
      } catch (e) {
        console.error('[API] Error calculating end time:', e);
      }
    }
    
    // Use the original start time without modifications
    // Previous code was: const formattedStartTime = bookingData.start_time.replace('Z', '.000Z');
    // which caused double milliseconds like '.000.000Z'
    const formattedStartTime = bookingData.start_time;
    console.log(`[API] Using original start time without modification: ${formattedStartTime}`);
    
    // Format the request payload according to Cal.com v1 API
    const requestPayload = {
      // Cal.com v1 API uses a different payload structure
      event_type_id: parseInt(bookingData.event_type_id),
      start_time: formattedStartTime,
      end_time: endTime,
      user_name: bookingData.user_name,
      user_email: bookingData.user_email,
      timezone: bookingData.timezone || 'UTC',
      notes: bookingData.notes || '',
      client_id: clientId,
      // These will be properly formatted in the backend:
      // - The backend will nest name/email into responses
      // - The backend will add location, language, etc.
    };
    
    // Log the complete request
    console.log('[API] Booking request payload:', JSON.stringify(requestPayload, null, 2));
    
    // Make the API call
    const response = await axios.post(apiUrl, requestPayload);
    console.log('[API] Booking response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('[API] Booking error:', error);
    
    // Parse the error to extract the detailed message from Cal.com API
    let errorMessage = 'An error occurred while booking the meeting. Please try again.';
    
    if (error.response?.data?.error) {
      // Direct error format from our API
      errorMessage = error.response.data.error;
    } else if (error.response?.data) {
      try {
        // Try to extract Cal.com specific error messages
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        }
      } catch (parseError) {
        console.error('[API] Error parsing error response:', parseError);
      }
    }
    
    // Throw an enhanced error with the extracted message
    const enhancedError = new Error(errorMessage);
    (enhancedError as any).originalError = error;
    throw enhancedError;
  }
};

// Fetch all bookings from Cal.com
export const getCalendarBookings = async (clientId: string): Promise<any> => {
  try {
    const apiUrl = `${API_BASE_URL}/api/calendar/bookings/${clientId}`;
    console.log('Fetching all calendar bookings from:', apiUrl);
    
    const response = await axios.get(apiUrl);
    console.log('Calendar bookings response:', response.data);
    
    // Return only the booked_times_by_day structure
    if (response.data && response.data.data) {
      return {
        booked_times_by_day: response.data.data.booked_times_by_day || {}
      };
    }
    
    return {
      booked_times_by_day: {}
    };
  } catch (error) {
    console.error('Error fetching calendar bookings:', error);
    // Return empty structure on error instead of throwing
    return {
      booked_times_by_day: {}
    };
  }
};

/**
 * Fetch chat history for a client
 * @param clientId The client ID
 * @param conversationId Optional conversation ID to filter by
 * @param limit Maximum number of messages to return (default 10)
 * @returns Promise that resolves to the chat history
 */
export const getChatHistory = async (
    clientId: string, 
    conversationId?: string,
    limit: number = 10
): Promise<any> => {
    try {
        let url = `${getApiBaseUrl()}/chat/history/${clientId}?limit=${limit}`;
        
        if (conversationId) {
            url += `&conversation_id=${conversationId}`;
        }
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'omit',
            mode: 'cors'
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        return result;
    } catch (error) {
        console.error('[CHAT-API] Error fetching chat history:', error);
        throw error;
    }
};

/**
 * Fetch chat history with citations for a client
 * @param clientId The client ID
 * @param page Current page number (1-indexed)
 * @param limit Maximum number of history items per page
 * @param searchQuery Optional search term to filter chat history
 * @returns Promise that resolves to the chat history with citations
 */
export const getChatHistoryWithCitations = async (
    clientId: string,
    options: {
        page?: number;
        limit?: number;
        searchQuery?: string;
    } = {}
): Promise<any> => {
    try {
        const { 
            page = 1, 
            limit = 10, 
            searchQuery = undefined 
        } = options;
        
        // Validate page and limit to prevent NaN errors
        const validPage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
        const validLimit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 10;
        
        // Calculate offset based on validated page and limit
        const offset = (validPage - 1) * validLimit;
        
        // Create search parameters with validated values
        const searchParams = new URLSearchParams({
            offset: offset.toString(),
            limit: validLimit.toString(),
            with_citations: 'true'
        });
        
        // Add search query if provided and not empty
        if (searchQuery && searchQuery.trim()) {
            searchParams.append('search', searchQuery.trim());
        }
        
        // Use axios - interceptors will automatically add authorization headers
        const response = await axios.get(
            `${API_BASE_URL}/api/client/${encodeURIComponent(clientId)}/chat-history?${searchParams.toString()}`
        );
        
        console.log('[CHAT-API] Chat history with citations retrieved successfully');
        return response.data;
    } catch (error: unknown) {
        console.error('[CHAT-API] Error fetching chat history with citations:', error);
        
        // Return structured error response instead of throwing
        return {
            success: false,
            message: error instanceof Error 
                ? error.message 
                : (error as any)?.response?.data?.message || 'Failed to fetch chat history',
            error: error
        };
    }
};

export const getCitationAnalytics = async (clientId: string) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/citations/analytics/${clientId}`);
    
    if (response.status !== 200) {
      console.error(`[CITATION-API] API returned error: ${response.status}`);
      return {
        success: false,
        message: `API returned status ${response.status}`,
        data: {
          totalCitations: 0,
          totalQueries: 0,
          topSources: [],
          citationsByDay: [],
          recentCitations: [],
          averageCitationsPerQuery: 0,
          citationRatio: 0
        }
      };

    }
    
    const data = response.data;
    return data;
  } catch (error) {
    console.error('[CITATION-API] Error fetching citation analytics:', error);
    throw error;
  }
};

export const getCitationSettings = async (clientId: string): Promise<any> => {
  try {
    console.log('[API] Fetching citation settings for client:', clientId);
    
    // Make the API call
    const response = await axios.get(`${API_BASE_URL}/api/citations/status/${clientId}`);
    console.log('[API] Raw citation settings response:', response.data);
    
    // Ensure we return a consistent format
    return {
      success: true,
      citations_enabled: response.data.citations_enabled === true
    };
  } catch (error) {
    console.error('[API] Error fetching citation settings:', error);
    return {
      success: false,
      citations_enabled: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Delete a specific chat conversation
 * @param clientId - The client ID
 * @param conversationId - The conversation ID to delete
 * @returns Promise that resolves to the deletion result
 */
export const deleteChatConversation = async (clientId: string, conversationId: string): Promise<any> => {
  try {
    console.log('[CHAT-API] Deleting conversation:', { clientId, conversationId });
    
    await axios.delete(`${API_BASE_URL}/api/chat/conversation/${clientId}/${conversationId}`);
    
    console.log('[CHAT-API] Conversation deleted successfully');
    return {
      success: true,
      message: 'Conversation deleted successfully'
    };
  } catch (error) {
    console.error('[CHAT-API] Error deleting conversation:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to delete conversation',
      error: error
    };
  }
};

/**
 * Clear all chat history for a client
 * @param clientId - The client ID
 * @returns Promise that resolves to the clear result
 */
export const clearAllChatHistory = async (clientId: string): Promise<any> => {
  try {
    console.log('[CHAT-API] Clearing all chat history for client:', clientId);
    
    await axios.delete(`${API_BASE_URL}/api/chat/history/${clientId}/clear-all`);
    
    console.log('[CHAT-API] All chat history cleared successfully');
    return {
      success: true,
      message: 'All chat history cleared successfully'
    };
  } catch (error) {
    console.error('[CHAT-API] Error clearing chat history:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to clear chat history',
      error: error
    };
  }
};

export const toggleCitationSettings = async (clientId: string, enabled: boolean): Promise<any> => {
  try {
    console.log(`[CITATION-API] Toggling citations for ${clientId} to:`, enabled);
    
    const response = await axios.post(`${API_BASE_URL}/api/citations/toggle/${clientId}`, {
      enable: enabled
    });

    const data = response.data;
    console.log('[CITATION-API] Toggle response:', data);
    
    // Check if the current state matches what we requested
    if (data.success && data.citations_enabled !== enabled) {
      console.warn('[CITATION-API] Warning: Server returned different state than requested', {
        requested: enabled,
        returned: data.citations_enabled
      });
    }
    
    return data;
  } catch (error) {
    console.error('[CITATION-API] Error toggling citation settings:', error);
    throw error;
  }
};

export const updateAllowedDomains = async (clientId: string, allowedDomains: string[]) => {
    const response = await axios.put(`${API_BASE_URL}/api/client/${clientId}/allowed-domains`, {
        allowed_domains: allowedDomains
    });
    return response.data;
};

// Client Management APIs
export const getAllClients = async () => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        // Decode JWT token to get consultant_id
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/consultant/${consultantId}/clients`);
        return response.data;
    } catch (error) {
        console.error('Error fetching clients:', error);
        throw new Error('Failed to fetch clients');
    }
};

export const updateClient = async (clientId: string, data: any) => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        // Decode JWT token to get consultant_id
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        
        const response = await axios.put(`${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}`, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to update client');
        }
        throw new Error('Failed to update client');
    }
};

export const deleteClient = async (clientId: string) => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        // Decode JWT token to get consultant_id
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        
        const response = await axios.delete(`${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to delete client');
        }
        throw new Error('Failed to delete client');
    }
};

// Role Management APIs
export const getRoles = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/roles`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch roles');
    }
};

export const createRole = async (data: { name: string; description: string; permissions: string[] }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/roles`, data);
        return response.data;
    } catch (error) {
        throw new Error('Failed to create role');
    }
};

export const getPermissions = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/permissions`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch permissions');
    }
};

export const updateRole = async (roleId: string, data: any) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/api/roles/${roleId}`, data);
        return response.data;
    } catch (error) {
        throw new Error('Failed to update role');
    }
};

export const deleteRole = async (roleId: string) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/api/roles/${roleId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to delete role');
    }
};

// Consultant Client APIs
export const getClientStats = async (clientId: string) => {
    try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        // Decode JWT token to get consultant_id
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const consultantId = tokenPayload.consultant_id;
        
        if (!consultantId) {
            throw new Error('Consultant ID not found in token');
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/consultant/${consultantId}/clients/${clientId}/stats`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch client statistics');
    }
};

export const getClientChatHistory = async (clientId: string, page: number, limit: number = 10) => {
    try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
            throw new Error('User ID not found');
        }
        const response = await axios.get(
            `${API_BASE_URL}/api/consultant/${userId}/clients/${clientId}/chat-history?page=${page}&limit=${limit}`
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to fetch chat history');
    }
};

export const impersonateClient = async (clientId: string) => {
    try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
            throw new Error('User ID not found');
        }
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/${userId}/impersonate/${clientId}`, 
            {}
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to impersonate client');
    }
};

// Consultant management functions
export const getAllConsultants = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/consultant/consultants`);
        return response.data;
    } catch (error) {
        console.error('Error fetching consultants:', error);
        throw error;
    }
};

export const getConsultantDetails = async (consultantId: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/consultant/consultants/${consultantId}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching consultant details for ${consultantId}:`, error);
        throw error;
    }
};

export const createConsultant = async (consultantData: {
    name: string;
    email: string;
    password: string;
}) => {
    try {
        console.log('Creating consultant with data:', consultantData);
        
        const response = await axios.post(
            `${API_BASE_URL}/api/consultant/consultants`,
            consultantData
        );

        console.log('Consultant creation response:', response.data);
        return response.data;
    } catch (error: any) {
        console.error('Error creating consultant:', error);
        
        // Handle specific error cases
        if (error.response?.status === 401) {
            throw new Error('Session expired. Please login again.');
        }
        
        // Email already exists - direct from FastAPI
        if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
            // Extract error message without status code prefix
            const errorDetail = error.response.data.detail;
            
            // Check if it contains the HTTP status code prefix (e.g., "400: ")
            if (errorDetail.match(/^\d{3}:\s/)) {
                // Remove the status code prefix (like "400: ")
                const cleanMessage = errorDetail.replace(/^\d{3}:\s/, '');
                throw new Error(cleanMessage);
            }
            
            // Check for email already exists message
            if (errorDetail.includes('Email already exists')) {
                throw new Error('This email address is already registered. Please use a different email address.');
            }
            
            // Otherwise return the original detail
            throw new Error(errorDetail);
        }
        
        // PostgreSQL duplicate key error from database
        if (error.response?.data?.detail?.includes('duplicate key value violates unique constraint')) {
            throw new Error('This email address is already registered. Please use a different email.');
        }
        
        // Fallback to a generic error message
        throw new Error(error.message || 'Failed to create consultant. Please try again.');
    }
};

export const updateConsultant = async (consultantId: string, consultantData: {
    name: string;
    email: string;
    password?: string;
}) => {
    try {
        console.log('Updating consultant with data:', consultantData);
        
        const response = await axios.put(
            `${API_BASE_URL}/api/consultant/consultants/${consultantId}`,
            consultantData
        );

        console.log('Consultant update response:', response.data);
        return response.data;
    } catch (error: any) {
        console.error(`Error updating consultant ${consultantId}:`, error);
        
        // Handle specific error cases
        if (error.response?.status === 401) {
            throw new Error('Session expired. Please login again.');
        }
        
        if (error.response?.status === 403) {
            throw new Error('Not authorized to update this consultant');
        }
        
        if (error.response?.status === 404) {
            throw new Error('Consultant not found');
        }
        
        // Email already exists error
        if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
            const errorDetail = error.response.data.detail;
            
            if (errorDetail.includes('Email already exists')) {
                throw new Error('This email address is already registered. Please use a different email address.');
            }
            
            throw new Error(errorDetail);
        }
        
        // Fallback to a generic error message
        throw new Error(error.message || 'Failed to update consultant. Please try again.');
    }
};

export const deleteConsultant = async (consultantId: string) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/api/consultant/consultants/${consultantId}`);
        return response.data;
    } catch (error: any) {
        console.error(`Error deleting consultant ${consultantId}:`, error);
        
        // Handle specific error cases
        if (error.response?.status === 401) {
            throw new Error('Session expired. Please login again.');
        }
        
        if (error.response?.status === 404) {
            throw new Error('Consultant not found');
        }
        
        if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
            throw new Error(error.response.data.detail);
        }
        
        throw new Error(error.message || 'Failed to delete consultant');
    }
};

// Add new function for paginated recent citations
export const getRecentCitationsPaginated = async (clientId: string, page: number, limit: number) => {
  try {
    // Use axios consistent with getCitationAnalytics
    const response = await axios.get(`${API_BASE_URL}/api/citations/recent/${clientId}?page=${page}&limit=${limit}`);
    return response.data; // axios returns data directly
  } catch (error) {
    console.error('Error fetching paginated recent citations:', error);
    // Re-throw or return a structured error based on existing patterns
    throw new Error('Failed to fetch recent citations'); 
  }
};

export const uploadDocumentsWithProgress = async (
    formData: FormData, 
    onProgress?: (data: any) => void,
    onError?: (error: any) => void
): Promise<any> => {
    try {
        // Make the initial upload request
        const response = await axios.post(`${API_BASE_URL}/api/documents/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        const responseData = response.data;
        
        // If progress tracking is requested and we got an upload_id, setup a stream
        if (onProgress && responseData.upload_id) {
            // Create stream connection to track upload progress
            const eventSource = createDocumentUploadStream(responseData.upload_id, onProgress, onError);
            
            // Attach the close method to the response
            responseData.closeStream = () => eventSource.close();
        }

        return responseData;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            throw new Error(error.response?.data?.detail || 'Failed to upload documents');
        }
        throw error;
    }
};

/**
 * Creates a server-sent event connection to monitor document upload status.
 * 
 * @param uploadId Upload ID to monitor
 * @param onMessage Optional callback for handling messages
 * @param onError Optional callback for errors
 * @returns EventSource object
 */
export const createDocumentUploadStream = (
    uploadId: string,
    onMessage?: (data: any) => void,
    onError?: (error: any) => void
): EventSource => {
    const streamUrl = `${API_BASE_URL}/api/documents/upload/${uploadId}/stream`;
    
    console.log(`[UPLOAD-API] Creating stream to: ${streamUrl}`);
    
    // Create EventSource for streaming updates
    const eventSource = new EventSource(streamUrl);
    
    // Flag to track if we've detected any error message
    let errorDetected = false;
    
    // If callbacks are provided, connect them
    if (onMessage) {
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                // Check for error message
                if (data.error) {
                    console.log(`[UPLOAD-API] Error detected: ${data.error}`);
                    errorDetected = true;
                    eventSource.close(); // Close the connection
                    
                    // Still call onMessage so client can handle this status
                    onMessage(data);
                    
                    // Call error handler if provided
                    if (onError) onError(data.error);
                    return;
                }
                
                // Process normal messages
                onMessage(data);
                
                // Check if upload is complete or failed
                if (data.status === 'completed' || data.status === 'failed') {
                    console.log(`[UPLOAD-API] Upload ${data.status}, closing stream`);
                    eventSource.close();
                }
            } catch (e) {
                console.error(`[UPLOAD-API] Error parsing event data:`, e);
                if (onError) onError(e);
            }
        };
    }
    
    // Custom error handler
    eventSource.onerror = (error) => {
        console.error(`[UPLOAD-API] Stream error:`, error);
        
        // Call the provided error callback
        if (onError) onError(error);
        
        // Close connection if we already detected an error to prevent reconnection
        if (errorDetected) {
            eventSource.close();
        }
    };
    
    return eventSource;
};

export const getClientDocuments = async (
    clientId: string,
    page: number = 1,
    limit: number = 50,
    search?: string
) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        if (search) params.append('search', search);
        const response = await axios.get(`${API_BASE_URL}/api/documents/client/${clientId}?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client documents:', error);
        throw new Error('Failed to fetch documents');
    }
};

// Add function to fetch document content by ID using existing endpoint
export const getDocumentContent = async (documentId: string) => {
    try {
        console.log(`Fetching content for document ID: ${documentId}`);
        const response = await axios.get(`${API_BASE_URL}/api/documents/${documentId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching document content:', error);
        throw new Error('Failed to fetch document content');
    }
};

// Delete a document
export const deleteDocument = async (documentId: string) => {
    try {
        console.log(`Deleting document ID: ${documentId}`);
        const response = await axios.delete(`${API_BASE_URL}/api/documents/${documentId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting document:', error);
        throw new Error('Failed to delete document');
    }
};

/**
 * =====================
 * THEME ENDPOINTS
 * =====================
 */

/**
 * Get active/default theme + layout for a client
 */
export const getClientTheme = async (clientId: string): Promise<any> => {
    if (!clientId) throw new Error('clientId is required');
    try {
        const response = await axios.get(`${getApiBaseUrl()}/themes/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching client theme', error);
        throw error;
    }
};

/**
 * Save / upsert default theme for a client
 */
export const saveClientTheme = async (clientId: string, payload: any): Promise<any> => {
    if (!clientId) throw new Error('clientId is required');
    try {
        const response = await axios.post(`${getApiBaseUrl()}/themes/${clientId}`, payload);
        return response.data;
    } catch (error) {
        console.error('[API] Error saving client theme', error);
        throw error;
    }
};

// QA Validation API Functions

/**
 * Generate Q&A pairs for client validation
 */
export const generateQAValidation = async (
    clientId: string,
    numQuestions: number = 10,
    opts?: {
        source_filters?: {
            page_urls?: string[];
            document_ids?: (string|number)[];
            youtube_ids?: (string|number)[];
        };
        generation_mode?: 'combined'|'per_source';
        custom_questions?: string[];
        options?: Record<string, any>;
    }
): Promise<any> => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/qa-validation/generate`, {
            client_id: clientId,
            num_questions: numQuestions,
            ...(opts?.source_filters ? { source_filters: {
                page_urls: opts.source_filters.page_urls || [],
                document_ids: (opts.source_filters.document_ids || []).map(String),
                youtube_ids: (opts.source_filters.youtube_ids || []).map(String)
            }} : {}),
            ...(opts?.generation_mode ? { generation_mode: opts.generation_mode } : {}),
            ...(opts?.custom_questions ? { custom_questions: opts.custom_questions } : {}),
            ...(opts?.options ? { options: opts.options } : {})
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error generating QA validation', error);
        throw error;
    }
};

/**
 * Generate Q&A pairs with SSE streaming for progress updates
 */
export const generateQAValidationStream = (
    clientId: string,
    numQuestions: number = 10,
    opts?: {
        source_filters?: {
            page_urls?: string[];
            document_ids?: (string | number)[];
            youtube_ids?: (string | number)[];
        };
        generation_mode?: 'combined' | 'per_source';
        custom_questions?: string[];
        options?: Record<string, any>;
    },
    callbacks?: {
        onProgress?: (data: any) => void;
        onQAPair?: (data: any) => void;
        onComplete?: (data: any) => void;
        onError?: (error: string) => void;
    }
): { abort: () => void } => {
    const controller = new AbortController();
    const { signal } = controller;

    const authToken = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
    };

    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }

    const postData = {
        client_id: clientId,
        num_questions: numQuestions,
        ...(opts?.source_filters ? {
            source_filters: {
                page_urls: opts.source_filters.page_urls || [],
                document_ids: (opts.source_filters.document_ids || []).map(String),
                youtube_ids: (opts.source_filters.youtube_ids || []).map(String)
            }
        } : {}),
        ...(opts?.generation_mode ? { generation_mode: opts.generation_mode } : {}),
        ...(opts?.custom_questions ? { custom_questions: opts.custom_questions } : {}),
        ...(opts?.options ? { options: opts.options } : {})
    };

    const apiUrl = `${getApiBaseUrl()}/qa-validation/generate/stream`;

    fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(postData),
        signal,
        credentials: 'omit',
        mode: 'cors'
    }).then(response => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Could not get response reader');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    let currentEventType = '';
                    for (const line of lines) {
                        if (line.startsWith('event:')) {
                            currentEventType = line.slice(6).trim();
                        } else if (line.startsWith('data:')) {
                            try {
                                const data = JSON.parse(line.slice(5).trim());

                                if (currentEventType === 'error' || data.event_type === 'error') {
                                    callbacks?.onError?.(data.error || 'Unknown error');
                                } else if (currentEventType === 'complete' || data.event_type === 'complete') {
                                    callbacks?.onComplete?.(data);
                                } else if (data.event_type === 'qa_pair' || data.event_type === 'question') {
                                    callbacks?.onQAPair?.(data);
                                } else {
                                    callbacks?.onProgress?.(data);
                                }
                            } catch (parseError) {
                                console.warn('[QA-API] Failed to parse SSE data:', line);
                            }
                        }
                    }
                }
            } catch (error: any) {
                if (error.name !== 'AbortError') {
                    console.error('[QA-API] Stream processing error:', error);
                    callbacks?.onError?.(error.message || 'Stream processing failed');
                }
            }
        };

        processStream();
    }).catch(error => {
        if (error.name !== 'AbortError') {
            console.error('[QA-API] Fetch error:', error);
            callbacks?.onError?.(error.message || 'Failed to start QA generation');
        }
    });

    return { abort: () => controller.abort() };
};

/**
 * Get QA validation sessions for a client
 */
export const getQAValidationSessions = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/qa-validation/sessions/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching QA validation sessions', error);
        throw error;
    }
};

/**
 * Update QA validation session status
 */
export const updateQASessionStatus = async (sessionId: string, status: string, reviewedBy: string): Promise<any> => {
    try {
        const response = await axios.put(`${getApiBaseUrl()}/qa-validation/session/status`, {
            session_id: sessionId,
            status: status,
            reviewed_by: reviewedBy
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error updating QA session status', error);
        throw error;
    }
};

/**
 * Get client QA validation status
 */
export const getClientQAStatus = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/qa-validation/client/${clientId}/status`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching client QA status', error);
        throw error;
    }
};

/**
 * Delete QA validation session
 */
export const deleteQASession = async (sessionId: string): Promise<any> => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/qa-validation/session/${sessionId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting QA session', error);
        throw error;
    }
};

/**
 * Submit QA validation feedback
 */
export const submitQAFeedback = async (sessionId: string, qaPairIndex: number, feedbackType: string, reason?: string, additionalComments?: string): Promise<any> => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/qa-validation/feedback`, {
            session_id: sessionId,
            qa_pair_index: qaPairIndex,
            feedback_type: feedbackType,
            reason: reason,
            additional_comments: additionalComments || ''
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error submitting QA feedback', error);
        throw error;
    }
};

/**
 * Get chat messages with feedback status
 */
export const getMessagesWithFeedback = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/api/feedback/${clientId}/messages`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching messages with feedback', error);
        throw error;
    }
};

/**
 * Get list of users for a client with conversation counts
 */
export const getClientUsers = async (clientId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/feedback/${clientId}/users`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching client users', error);
        throw error;
    }
};

/**
 * Get conversations for a specific user
 */
export const getUserConversations = async (clientId: string, userId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/feedback/${clientId}/users/${userId}/conversations`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching user conversations', error);
        throw error;
    }
};

/**
 * Get all conversations for a client with pagination
 */
export const getAllConversations = async (clientId: string, offset: number = 0, limit: number = 50): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/feedback/${clientId}/conversations`, {
            params: { offset, limit }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching all conversations', error);
        throw error;
    }
};

/**
 * Get all messages in a specific conversation
 */
export const rejectConversation = async (clientId: string, conversationId: string, reason: string): Promise<any> => {
    try {
        const reviewerId = localStorage.getItem('user_id') || 'system';
        
        const response = await axios.post(
            `${getApiBaseUrl()}/feedback/${clientId}/conversations/${conversationId}/reject`,
            {
                rejected_by: reviewerId,
                rejection_reason: reason
            }
        );

        return response.data;
    } catch (error: any) {
        console.error('Error rejecting conversation:', error);
        throw error;
    }
};

export const getConversationRejectionStatus = async (clientId: string, conversationId: string): Promise<any> => {
    try {
        const response = await axios.get(
            `${getApiBaseUrl()}/feedback/${clientId}/conversations/${conversationId}/rejection-status`
        );

        return response.data;
    } catch (error: any) {
        console.error('Error getting conversation rejection status:', error);
        throw error;
    }
};

export const getConversationMessages = async (clientId: string, userId: string, conversationId: string): Promise<any> => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/feedback/${clientId}/users/${userId}/conversations/${conversationId}/messages`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching conversation messages', error);
        throw error;
    }
};

export const deleteConversationMessage = async (
    clientId: string,
    userId: string,
    conversationId: string,
    messageId: string
): Promise<any> => {
    try {
        const response = await axios.delete(
            `${getApiBaseUrl()}/feedback/${clientId}/users/${userId}/conversations/${conversationId}/messages/${messageId}`
        );
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting conversation message', error);
        throw error;
    }
};

// SEO Analysis Functions
export const runSEOAnalysis = async (clientId: string, analysisType: string = 'full') => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/seo/analyze`, {
            client_id: clientId,
            analysis_type: analysisType
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error running SEO analysis', error);
        throw error;
    }
};

export const startSEOAnalysisSession = async (clientId: string, analysisType: string = 'full', sessionName?: string) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/seo/start-session`, {
            client_id: clientId,
            analysis_type: analysisType,
            session_name: sessionName
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error starting SEO analysis session', error);
        throw error;
    }
};

export const getSEOAnalysisSessionStatus = async (sessionId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/session/${sessionId}/status`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting SEO analysis session status', error);
        throw error;
    }
};

export const streamSEOAnalysisStatus = (sessionId: string, onMessage: (data: any) => void, onError?: (error: any) => void) => {
    const eventSource = new EventSource(`${getApiBaseUrl()}/seo/session/${sessionId}/stream`);
    
    eventSource.onmessage = (event) => {
        try {
            const data = JSON.parse(event.data);
            onMessage(data);
        } catch (error) {
            console.error('[API] Error parsing SEO stream data:', error);
            if (onError) onError(error);
        }
    };
    
    eventSource.addEventListener('close', () => {
        console.log('[API] SEO analysis stream closed');
        eventSource.close();
    });
    
    eventSource.onerror = (error) => {
        console.error('[API] SEO analysis stream error:', error);
        if (onError) onError(error);
        eventSource.close();
    };
    
    return eventSource;
};

export const getClientSEOSummary = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/client/${clientId}/summary`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting client SEO summary', error);
        throw error;
    }
};

export const getClientSEOPages = async (clientId: string, pageUrl?: string) => {
    try {
        const url = pageUrl 
            ? `${getApiBaseUrl()}/seo/client/${clientId}/pages?page_url=${encodeURIComponent(pageUrl)}`
            : `${getApiBaseUrl()}/seo/client/${clientId}/pages`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting client SEO pages', error);
        throw error;
    }
};

export const getClientSEORecommendations = async (clientId: string, priority?: string) => {
    try {
        const url = priority 
            ? `${getApiBaseUrl()}/seo/client/${clientId}/recommendations?priority=${priority}`
            : `${getApiBaseUrl()}/seo/client/${clientId}/recommendations`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting client SEO recommendations', error);
        throw error;
    }
};

export const getClientAIReadiness = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/client/${clientId}/ai-readiness`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting client AI readiness', error);
        throw error;
    }
};

export const getPageSEODetails = async (clientId: string, pageUrl: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/client/${clientId}/pages/${encodeURIComponent(pageUrl)}/details`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting page SEO details', error);
        throw error;
    }
};

export const deleteClientSEOAnalysis = async (clientId: string) => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/seo/client/${clientId}/analysis`);
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting client SEO analysis', error);
        throw error;
    }
};

// Real-time SEO APIs
export const getLiveVisitors = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/live-visitors/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting live visitors', error);
        throw error;
    }
};

export const getKeywordRankings = async (clientId: string, limit: number = 50) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/keyword-rankings/${clientId}?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting keyword rankings', error);
        throw error;
    }
};

export const getBacklinkAnalytics = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/backlinks/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting backlink analytics', error);
        throw error;
    }
};

export const getCompetitorAnalysis = async (clientId: string, limit: number = 5) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/competitors/${clientId}?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting competitor analysis', error);
        throw error;
    }
};

export const getPageSpeed = async (clientId: string, pageUrl?: string) => {
    try {
        const url = pageUrl 
            ? `${getApiBaseUrl()}/seo/realtime/page-speed/${clientId}?page_url=${encodeURIComponent(pageUrl)}`
            : `${getApiBaseUrl()}/seo/realtime/page-speed/${clientId}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting page speed', error);
        throw error;
    }
};

export const getSERPFeatures = async (clientId: string, keywords: string[]) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/seo/realtime/serp-features`, {
            keywords,
            client_id: clientId
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error getting SERP features', error);
        throw error;
    }
};

export const getSEODashboardData = async (clientId: string, forceRefresh: boolean = false) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/seo/realtime/seo-dashboard/${clientId}`, {
            params: { force_refresh: forceRefresh }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error getting SEO dashboard data', error);
        throw error;
    }
};

// YouTube Video APIs
export const getYouTubeVideos = async (
    clientId: string,
    {
        status,
        page = 1,
        limit = 50,
        search
    }: { status?: string; page?: number; limit?: number; search?: string } = {}
) => {
    try {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (status) params.append('status', status);
        if (search) params.append('search', search);
        const url = `${getApiBaseUrl()}/youtube/videos/${clientId}?${params.toString()}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting YouTube videos', error);
        throw error;
    }
};

export const processYouTubeVideo = async (clientId: string, url: string, reviewRequired: boolean = true) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/process/${clientId}`, {
            url,
            review_required: reviewRequired
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error processing YouTube video', error);
        throw error;
    }
};

export const syncYouTubeChannel = async (clientId: string, channelUrl: string) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/channel/${clientId}`, {
            channel_url: channelUrl
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error syncing YouTube channel', error);
        throw error;
    }
};

export const getYouTubeVideoTranscript = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/transcript`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting video transcript', error);
        throw error;
    }
};

export const fetchYouTubeTranscript = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/fetch-transcript`);
        return response.data;
    } catch (error) {
        console.error('[API] Error fetching YouTube transcript', error);
        throw error;
    }
};

export const getYouTubeTranscriptionStatus = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/transcription-status`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting YouTube transcription status', error);
        throw error;
    }
};

export const approveYouTubeVideo = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/approve`);
        return response.data;
    } catch (error) {
        console.error('[API] Error approving YouTube video', error);
        throw error;
    }
};

export const deleteYouTubeVideo = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting YouTube video', error);
        throw error;
    }
};

export const retranscribeYouTubeVideo = async (clientId: string, videoId: number) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/youtube/video/${clientId}/${videoId}/retranscribe`);
        return response.data;
    } catch (error) {
        console.error('[API] Error re-transcribing YouTube video', error);
        throw error;
    }
};

export const getYouTubeProcessingStatus = async (clientId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/youtube/status/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting YouTube processing status', error);
        throw error;
    }
};

// ==================== Billing & Subscription API ====================

// Get subscription plans
export const getSubscriptionPlans = async () => {
    try {
        const userRole = localStorage.getItem('user_role');
        const includePrivate = userRole === 'super';
        const response = await axios.get(`${getApiBaseUrl()}/billing/plans${includePrivate ? '?include_private=true' : ''}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting subscription plans', error);
        throw error;
    }
};

// Get current subscription
export const getCurrentSubscription = async () => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/billing/subscription`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting current subscription', error);
        throw error;
    }
};

// Create a new subscription
export const createSubscription = async (data: {
    plan_id: string;
    billing_period: 'monthly' | 'yearly';
    payment_method_id?: string;
    promo_code?: string;
}) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/billing/subscription`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error creating subscription', error);
        throw error;
    }
};

// Cancel subscription
export const cancelSubscription = async (at_period_end: boolean = true, reason?: string) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/billing/subscription/cancel`, {
            at_period_end,
            reason
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error canceling subscription', error);
        throw error;
    }
};

// Create checkout session
export const createCheckoutSession = async (data: { plan_id: string; billing_period: string }) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/billing/checkout`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error creating checkout session', error);
        throw error;
    }
};

// Get usage data
export const getUsageData = async () => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/billing/usage`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting usage data', error);
        throw error;
    }
};

// Get payment methods
export const getPaymentMethods = async () => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/billing/payment-methods`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting payment methods', error);
        throw error;
    }
};

// Add payment method
export const addPaymentMethod = async (payment_method_id: string, set_as_default: boolean = false) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/billing/payment-methods`, {
            payment_method_id,
            set_as_default
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error adding payment method', error);
        throw error;
    }
};

// Remove payment method
export const removePaymentMethod = async (payment_method_id: string) => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/billing/payment-methods/${payment_method_id}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error removing payment method', error);
        throw error;
    }
};

// Get invoices
export const getInvoices = async (limit: number = 10) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/billing/invoices?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting invoices', error);
        throw error;
    }
};

// Get consultant usage with client breakdown
export const getConsultantUsage = async () => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/billing/consultant-usage`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting consultant usage', error);
        throw error;
    }
};

export const processClientOverage = async (clientId: string, dryRun: boolean = true) => {
    try {
        const params = new URLSearchParams();
        params.append('dry_run', dryRun ? 'true' : 'false');
        if (clientId) {
            params.append('client_id', clientId);
        }

        const url = `${getApiBaseUrl()}/billing/admin/billing/process-overages?${params.toString()}`;
        const response = await axios.post(url);
        return response.data;
    } catch (error: any) {
        console.error('[API] Error processing overage billing', error);
        throw error?.response?.data || error;
    }
};

// Create customer portal session
export const createCustomerPortalSession = async (returnUrl: string) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/billing/customer-portal`, {
            return_url: returnUrl
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error creating customer portal session', error);
        throw error;
    }
};

// Create usage alert
export const createUsageAlert = async (data: {
    metric: string;
    alert_type: string;
    threshold_percentage?: number;
    threshold_value?: number;
    notification_emails?: string[];
}) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/billing/usage-alerts`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error creating usage alert', error);
        throw error;
    }
};

// Plan management (super user only)
export const getAllPlans = async (includePrivate: boolean = false) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/plan-management/plans`, {
            params: { include_private: includePrivate }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error getting plans', error);
        throw error;
    }
};

export const getPlanDetails = async (planId: string) => {
    try {
        const response = await axios.get(`${getApiBaseUrl()}/plan-management/plans/${planId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error getting plan details', error);
        throw error;
    }
};

export const createPlan = async (data: any) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/plan-management/plans`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error creating plan', error);
        throw error;
    }
};

export const updatePlan = async (planId: string, data: any) => {
    try {
        const response = await axios.put(`${getApiBaseUrl()}/plan-management/plans/${planId}`, data);
        return response.data;
    } catch (error) {
        console.error('[API] Error updating plan', error);
        throw error;
    }
};

export const deletePlan = async (planId: string, force: boolean = false) => {
    try {
        const response = await axios.delete(`${getApiBaseUrl()}/plan-management/plans/${planId}`, {
            params: { force }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error deleting plan', error);
        throw error;
    }
};

export const duplicatePlan = async (planId: string, newName: string) => {
    try {
        const response = await axios.post(`${getApiBaseUrl()}/plan-management/plans/${planId}/duplicate`, null, {
            params: { new_name: newName }
        });
        return response.data;
    } catch (error) {
        console.error('[API] Error duplicating plan', error);
        throw error;
    }
};

// Products API
export const getProducts = async (
    clientId: string,
    category?: string,
    page: number = 1,
    limit: number = 50
) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        if (category) {
            params.append('category', category);
        }
        const response = await axios.get(
            `${API_BASE_URL}/api/products/${clientId}?${params.toString()}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Products API error:', error);
        throw error;
    }
};

export const getProductCategories = async (clientId: string) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/products/${clientId}/categories`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Product categories API error:', error);
        throw error;
    }
};

export const getProductCategoryTree = async (clientId: string) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/api/products/${clientId}/category-tree`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Product category tree API error:', error);
        throw error;
    }
};

export const getProductDetail = async (clientId: string, url: string) => {
    try {
        const params = new URLSearchParams({ url });
        const response = await axios.get(
            `${API_BASE_URL}/api/products/${clientId}/product?${params.toString()}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Product detail API error:', error);
        throw error;
    }
};

export const cleanKnowledgeBase = async (clientId: string) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/api/knowledge-base/clean/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('[API] Error cleaning knowledge base', error);
        throw error;
    }
};
